// A bullet list in a plain textarea, read back by the PDF (`service_report_pdf_service.outline`):
// a mark and a space make an item, its indentation against the items above it the level.
export const INDENT = '    ';
export const MARKS = ['•', '◦', '▪'];
export const MAX_LEVEL = MARKS.length - 1;

const ITEM = /^([ \t]*)[-*+•◦▪–·][ \t]+/;
const MARKED = /^[-*+•◦▪–·](?:\s+(.*))?$/;

const lineStart = (value, i) => value.lastIndexOf('\n', i - 1) + 1;
const lineEnd = (value, i) => {
    const n = value.indexOf('\n', i);
    return n === -1 ? value.length : n;
};

function place(indents, indent) {
    while (indents.length && indent < indents[indents.length - 1]) indents.pop();
    if (!indents.length || (indent > indents[indents.length - 1] && indents.length <= MAX_LEVEL)) {
        indents.push(indent);
    }
    return indents.length - 1;
}

/** Each line as {kind, level} by the PDF's rule, mirrored there: extend both. `more` continues an
 *  item; an empty item is a `gap` that keeps the level it was typed at. */
export function outlineLines(lines) {
    let indents = [];
    let last = null;
    return lines.map(raw => {
        const line = raw.replace(/\t/g, INDENT).replace(/\s+$/, '');
        const text = line.replace(/^ +/, '');
        const indent = line.length - text.length;
        const marked = MARKED.exec(text);
        if (!text || (marked && !(marked[1] || '').trim())) {
            last = null;
            return { kind: 'gap', level: marked ? place([...indents], indent) : null };
        }
        if (marked) {
            last = 'item';
            return { kind: 'item', level: place(indents, indent) };
        }
        if (last && (indent || last === 'text')) return { kind: last === 'item' ? 'more' : 'text', level: null };
        indents = [];
        last = 'text';
        return { kind: 'text', level: null };
    });
}

export const itemPrefix = level => INDENT.repeat(level) + MARKS[level] + ' ';

/** The caret's line with the outline of everything up to it. */
function lineAt(value, pos) {
    const from = lineStart(value, pos);
    const lines = value.slice(0, lineEnd(value, pos)).split('\n');
    const index = lines.length - 1;
    const m = ITEM.exec(lines[index]);
    return { from, line: lines[index], lines, outline: outlineLines(lines), index, prefix: m ? m[0] : null };
}

function caretOnItem({ value, start, end }) {
    if (start !== end) return null;
    const at = lineAt(value, start);
    const { level } = at.outline[at.index];
    if (!at.prefix || level === null || start - at.from < at.prefix.length) return null;
    return { ...at, level, text: at.line.slice(at.prefix.length) };
}

const caretEdit = (from, to, text, at = from + text.length) => ({ from, to, text, selStart: at, selEnd: at });

/** Enter: the next item at the same level; on an empty item, one level up or out of the list. */
export function continueList(state) {
    const hit = caretOnItem(state);
    if (!hit) return continueAfterBreak(state);
    if (!hit.text.trim()) {
        return caretEdit(hit.from, hit.from + hit.line.length, hit.level ? itemPrefix(hit.level - 1) : '');
    }
    return caretEdit(state.start, state.end, `\n${hit.prefix}`);
}

/** Enter on a line added with Shift+Enter: the next item of the list it belongs to. */
function continueAfterBreak({ value, start, end }) {
    if (start !== end) return null;
    const { lines, outline, index } = lineAt(value, start);
    let i = index;
    while (i >= 0 && outline[i].kind === 'more') i -= 1;
    const owner = i < index && i >= 0 ? ITEM.exec(lines[i]) : null;
    return owner ? caretEdit(start, end, `\n${owner[0]}`) : null;
}

/** Shift+Enter: a second line inside the item, which the PDF sets under its text. */
export function breakInItem(state) {
    const hit = caretOnItem(state);
    return hit ? caretEdit(state.start, state.end, `\n${ITEM.exec(hit.line)[1]}  `) : null;
}

/** Tab / Shift+Tab on every selected item, at most one level below the item above it.
 *  Null when that leaves the key to the browser: no item selected, or nothing to outdent. */
export function shiftLevel({ value, start, end }, delta) {
    const from = lineStart(value, start);
    const to = lineEnd(value, end > start && value[end - 1] === '\n' ? end - 1 : end);
    const all = value.slice(0, to).split('\n');
    const outline = outlineLines(all);
    const first = value.slice(0, from).split('\n').length - 1;
    const lines = all.slice(first);
    if (!outline.slice(first).some(({ level }) => level !== null)) return null;

    let i = first - 1;
    while (i >= 0 && outline[i].level === null && outline[i].kind !== 'text') i -= 1;
    let ceiling = i >= 0 && outline[i].level !== null ? outline[i].level + 1 : 0;
    let changed = false;
    const out = lines.map((line, k) => {
        const { kind, level } = outline[first + k];
        if (level === null) {
            if (kind === 'text') ceiling = 0;
            return line;
        }
        const next = Math.max(0, Math.min(level + delta, ceiling, MAX_LEVEL));
        ceiling = next + 1;
        if (next === level || !ITEM.test(line)) return line;
        changed = true;
        return itemPrefix(next) + line.replace(ITEM, '');
    });
    if (!changed && delta < 0) return null;
    return replaceLines(start, end, from, to, lines, out);
}

/** The list button: every selected line an item, or plain text again if all of them are. */
export function toggleList({ value, start, end }) {
    const from = lineStart(value, start);
    const to = lineEnd(value, end > start && value[end - 1] === '\n' ? end - 1 : end);
    const lines = value.slice(from, to).split('\n');
    const filled = lines.filter(line => line.trim());
    const allItems = filled.length > 0 && filled.every(line => ITEM.test(line));
    const out = lines.map(line => {
        const m = ITEM.exec(line);
        if (allItems) return m ? line.slice(m[0].length) : line;
        if (m || (!line.trim() && lines.length > 1)) return line;
        return itemPrefix(0) + line.trimStart();
    });
    return replaceLines(start, end, from, to, lines, out);
}

/** "- " or "* " typed at the start of a line becomes the mark of its level. */
export function autoFormat({ value, start, end }) {
    if (start !== end) return null;
    const from = lineStart(value, start);
    const m = /^([ \t]*)[-*]$/.exec(value.slice(from, start));
    if (!m) return null;
    const lines = value.slice(0, from).split('\n');
    lines[lines.length - 1] = `${m[1]}- x`;
    return caretEdit(from, start, itemPrefix(outlineLines(lines).pop().level));
}

/** Backspace right after the mark: one level up, or the mark gone at the top level. */
export function removeMark(state) {
    const hit = caretOnItem(state);
    if (!hit || state.start - hit.from !== hit.prefix.length) return null;
    return caretEdit(hit.from, hit.from + hit.prefix.length, hit.level ? itemPrefix(hit.level - 1) : '');
}

function replaceLines(start, end, from, to, before, after) {
    const text = after.join('\n');
    if (before.length === 1) {
        // One line: the caret keeps its place in the text, whatever the prefix became.
        const shift = text.length - before[0].length;
        const clamp = i => Math.max(from, Math.min(from + text.length, i + shift));
        return { from, to, text, selStart: clamp(start), selEnd: clamp(end) };
    }
    return { from, to, text, selStart: from, selEnd: from + text.length };
}

/** `edit` applied as typing, so the browser's undo keeps it; false where that is unsupported. */
export function typeEdit(textarea, { from, to, text }) {
    if (typeof document.execCommand !== 'function') return false;
    textarea.setSelectionRange(from, to);
    try {
        return document.execCommand(text ? 'insertText' : 'delete', false, text);
    } catch {
        return false;
    }
}

export const applyToValue = (value, { from, to, text }) => value.slice(0, from) + text + value.slice(to);
